import type { MealIngredient, MealRecipe, PlannerRecipeDraft } from './types'

const PANTRY =
  /salt|pepper|oil|flour|sugar|vinegar|soy|broth|stock|pasta|rice|bean|spice|powder|seasoning|honey|mustard|sauce|masala|cumin|turmeric|chili|chilli|coriander|cinnamon|paprika/
const DAIRY = /milk|butter|cheese|cream|yogurt|egg/
const MEAT = /chicken|beef|pork|turkey|fish|salmon|shrimp|bacon|sausage|steak|lamb/

export function categoryFor(name: string) {
  const value = name.toLowerCase()
  if (MEAT.test(value)) return 'Meat / Seafood'
  if (DAIRY.test(value)) return 'Dairy'
  if (PANTRY.test(value)) return 'Pantry'
  return 'Produce'
}

export function formatAmount(value: number) {
  if (!value) return ''
  const abs = Math.abs(value)
  const whole = Math.floor(abs + 1e-6)
  const fraction = abs - whole
  const fractions: Array<[number, string]> = [
    [0.25, '1/4'],
    [1 / 3, '1/3'],
    [0.5, '1/2'],
    [2 / 3, '2/3'],
    [0.75, '3/4'],
  ]
  const match = fractions.find(([amount]) => Math.abs(fraction - amount) < 0.05)
  if (match && whole) return `${whole} ${match[1]}`
  if (match) return match[1]
  const rounded = Math.round(abs * 100) / 100
  return String(rounded)
}

export function scaledQuantity(
  ingredient: Pick<MealIngredient, 'quantity' | 'amount'>,
  servings: number,
  baseServings: number
) {
  if (!ingredient.amount || !baseServings) return ingredient.quantity
  return formatAmount(ingredient.amount * (servings / baseServings))
}

function parseQuantity(quantity?: string) {
  if (!quantity) return 0
  const text = quantity.trim()
  const mixed = text.match(/^(\d+)\s+(\d+)\s*\/\s*(\d+)$/)
  if (mixed) return Number(mixed[1]) + Number(mixed[2]) / Number(mixed[3])
  const fraction = text.match(/^(\d+)\s*\/\s*(\d+)$/)
  if (fraction) return Number(fraction[1]) / Number(fraction[2])
  const value = Number(text)
  return Number.isFinite(value) ? value : 0
}

function newId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID()
  return `meal-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

type ApiIngredient = {
  record_id?: number | string | null
  sort_order?: number | null
  whole_number?: number | null
  numerator?: number | null
  denominator?: number | null
  measurement_amount?: string | number | null
  raw_name?: string | null
  ingredient?: { ingredient_name?: string | null } | null
  unit_of_measurement?: { unit_abbreviation?: string | null; unit_name?: string | null } | null
}

export type ApiRecipe = {
  recipe_id?: number | string | null
  slug?: string | null
  recipe_name?: string | null
  recipe_img_url?: string | null
  resourcePath?: string | null
  number_of_servings?: number | null
  cook_time?: number | null
  preparation_time?: number | null
  total_time?: number | null
  recipe_image?: { resource_path?: string | null } | null
  recipe_ingredients?: Array<ApiIngredient | null> | null
}

function amountFromIngredient(item: ApiIngredient) {
  const whole = Number(item.whole_number) || 0
  const numerator = Number(item.numerator) || 0
  const denominator = Number(item.denominator) || 0
  const fromParts = whole + (denominator ? numerator / denominator : 0)
  if (fromParts) return fromParts
  const measured = Number(item.measurement_amount)
  return Number.isFinite(measured) ? measured : 0
}

function quantityLabel(item: ApiIngredient, amount: number) {
  if (amount) return formatAmount(amount)
  return ''
}

export function mealRecipeFromApi(recipe: ApiRecipe, fallbackImage?: string): MealRecipe {
  const servings = Number(recipe.number_of_servings) || 1
  const ingredients = [...(recipe.recipe_ingredients || [])]
    .filter((item): item is ApiIngredient => Boolean(item))
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
    .map((item) => {
      const name = item.ingredient?.ingredient_name || item.raw_name || 'Ingredient'
      const amount = amountFromIngredient(item)
      return {
        id: String(item.record_id || newId()),
        name,
        quantity: quantityLabel(item, amount),
        unit: item.unit_of_measurement?.unit_abbreviation || item.unit_of_measurement?.unit_name || '',
        category: categoryFor(name),
        amount,
      }
    })

  return {
    id: String(recipe.recipe_id),
    name: recipe.recipe_name || 'Recipe',
    image:
      recipe.resourcePath ||
      recipe.recipe_img_url ||
      recipe.recipe_image?.resource_path ||
      fallbackImage ||
      '',
    slug: recipe.slug || undefined,
    // 0 means the recipe lists no time.
    cookTime: Number(recipe.cook_time ?? recipe.total_time) || 0,
    prepTime: Number(recipe.preparation_time) || 0,
    servings,
    baseServings: servings,
    ingredients,
    shoppingContributions: [],
  }
}

export function mealRecipeFromDraft(draft: PlannerRecipeDraft): MealRecipe {
  const servings = draft.servings && draft.servings > 0 ? draft.servings : 1
  return {
    id: String(draft.id),
    name: draft.name || 'Recipe',
    image: draft.image || '',
    slug: draft.slug,
    cookTime: draft.cookTime || 0,
    prepTime: draft.prepTime || 0,
    servings,
    baseServings: servings,
    shoppingContributions: [],
    ingredients: draft.ingredients
      .filter((item) => item.name.trim())
      .map((item) => {
        const amount = item.amount ?? parseQuantity(item.quantity)
        return {
          id: newId(),
          name: item.name.trim(),
          quantity: item.quantity || (amount ? formatAmount(amount) : ''),
          unit: item.unit || '',
          category: categoryFor(item.name),
          amount,
        }
      }),
  }
}
